import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { useSearch } from "@tanstack/solid-router";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@fleet/ui";
import { Button, buttonVariants } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { api, createApiQuery, SERVER_BASE_URL } from "@/lib/api";
import { ServerDataGate } from "@/components/ui/server-data-gate";
import { queryClient } from "@/lib/query-client";
import { queryKeys, sharedApi } from "@space/shared";
import { showToast } from "@/components/ui/toast";
import { Link2, ShieldCheck } from "@fleet/icons";

type ProviderId = "google" | "apple";

const PROVIDERS_STALE_TIME_MS = 5 * 60 * 1000;

const PROVIDER_LABELS: Record<ProviderId, string> = {
  google: "Google",
  apple: "Apple",
};

/**
 * "Connected accounts": allows linking/unlinking Google and Apple from the
 * profile. Only shows providers ENABLED on the backend. Respects the
 * last-method safeguard (the backend refuses to leave the user with no access).
 */
export function ConnectedAccountsSection() {
  const { t } = useI18n();
  // Reads ?linked=<provider> to show feedback after returning from the callback.
  const search = useSearch({ strict: false }) as () => { linked?: string };
  const [confirmUnlink, setConfirmUnlink] = createSignal<ProviderId | null>(null);
  const [busy, setBusy] = createSignal(false);

  // P23/I2 · WRAPPED query (`createApiQuery`), NOT `useQuery`: both are read
  // during render (`enabledProviders`, `linkedProviders`, `hasPassword`)
  // and with `retry: false` a failure left `.data` valueless FOREVER — the
  // read suspended the entire profile screen against the route's boundary.
  // P23/CC-60 · P26. The `queryFn` wrote `data ?? {google:false,apple:false}`
  // over `openapi-fetch`, which NEVER throws: a 503 resolved as SUCCESS
  // with a FABRICATED object, `isError` never arrived, and the card
  // claimed "Not connected" / "This is your only sign-in method" for an
  // account that could well be linked. `createApiQuery` → `handleApiResponse`, which throws.
  const providersQuery = createApiQuery(
    () => queryKeys.auth.socialProviders(),
    async () => sharedApi.socialProviders(api),
    () => ({
      staleTime: PROVIDERS_STALE_TIME_MS,
      retry: false,
    }),
  );

  const methodsQuery = createApiQuery(
    () => queryKeys.auth.loginMethods(),
    async () => sharedApi.loginMethods(api),
    () => ({ retry: false }),
  );

  /**
   * P23/CC-60 · P26, the piece that makes the rest REACHABLE: the section
   * must ALSO show when the load failed. Without this, on error
   * `enabledProviders()` is `[]` and the OUTER `<Show>` hides the ENTIRE
   * card — neither content nor error. MEASURED in this repo: with
   * `providers` returning 503 CUT BEFORE registration, the profile
   * rendered (1777 characters) and the "Connected accounts" header showed
   * up ZERO times. The symptom isn't "missing error message": it's that
   * the section doesn't appear at all.
   */
  const hasLoadFailure = () => providersQuery.isError || methodsQuery.isError;

  const enabledProviders = createMemo<ProviderId[]>(() => {
    if (!providersQuery.isSuccess) {
      return [];
    }
    const p = providersQuery.data;
    if (!p) {
      return [];
    }
    const list: ProviderId[] = [];
    if (p.google) {
      list.push("google");
    }
    if (p.apple) {
      list.push("apple");
    }
    return list;
  });

  // `.data` ONLY under `isSuccess`: outside it there's no data, and
  // claiming "not connected" / "it's your only method" with the response
  // absent is exactly the lie this assignment exists to fix.
  const linkedProviders = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.providers ?? []) : [],
  );
  const hasPassword = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.has_password ?? false) : false,
  );

  const isLinked = (provider: ProviderId) => linkedProviders().includes(provider);

  // Total number of access methods (password + each linked social provider).
  const totalMethods = createMemo(() => linkedProviders().length);

  // Is this the LAST method? (can't unlink without setting another one first).
  const isLastMethod = (provider: ProviderId) =>
    isLinked(provider) && totalMethods() <= 1 && !hasPassword();

  const linkUrl = (provider: ProviderId) =>
    `${SERVER_BASE_URL.replace(/\/$/, "")}/api/auth/link-social/${provider}`;

  const handleUnlink = async (provider: ProviderId) => {
    setBusy(true);
    try {
      const { error } = await sharedApi.unlinkAccount(api, provider);
      if (error) {
        const message = error.message ?? "";
        if (message.includes("only sign-in method")) {
          showToast(t(msg`Set a password before unlinking your only sign-in method.`), "error");
        } else {
          showToast(t(msg`Could not unlink the account. Please try again.`), "error");
        }
        return;
      }
      showToast(t(msg`Account unlinked.`), "success");
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    } finally {
      setBusy(false);
      setConfirmUnlink(null);
    }
  };

  // Feedback when returning from the linking flow (?linked=<provider>).
  let linkedFeedbackDone = false;
  createEffect(() => {
    const provider = search().linked;
    if (provider && !linkedFeedbackDone) {
      linkedFeedbackDone = true;
      showToast(t(msg`Account connected.`), "success");
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    }
  });

  return (
    <Show when={enabledProviders().length > 0 || hasLoadFailure()}>
      <Card>
        <CardHeader class="flex flex-row items-center gap-2 space-y-0">
          <ShieldCheck class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Connected accounts`)}</CardTitle>
        </CardHeader>
        <CardContent class="space-y-3">
          {/* P23/I3 · with `login-methods` down, `isPending` is FALSE and
              `.data` has no value: the screen said "Not connected" for an
              account that WAS. "No response" ≠ "not connected": the error
              is painted as an error, with retry. */}
          <ServerDataGate
            testId="connected-accounts-error"
            pending={methodsQuery.isPending || providersQuery.isPending}
            error={hasLoadFailure()}
            refetch={() => {
              // Retries ONLY the one(s) that failed.
              if (providersQuery.isError) {
                void providersQuery.refetch();
              }
              if (methodsQuery.isError) {
                void methodsQuery.refetch();
              }
            }}
            skeleton={
              <div class="space-y-3">
                <Skeleton class="h-14 w-full rounded-xl" />
                <Skeleton class="h-14 w-full rounded-xl" />
              </div>
            }
          >
            <For each={enabledProviders()}>
              {(provider) => (
                <div class="flex items-center gap-4 rounded-xl border border-border bg-muted/30 p-4">
                  <div class="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <Link2 class="h-5 w-5" />
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-semibold">{PROVIDER_LABELS[provider]}</p>
                    <Show
                      when={isLinked(provider)}
                      fallback={
                        <p class="text-xs text-muted-foreground">{t(msg`Not connected`)}</p>
                      }
                    >
                      <Badge
                        variant="secondary"
                        class="mt-1 h-5 border-none bg-primary/20 px-1.5 text-[10px] font-bold uppercase tracking-wider text-primary"
                      >
                        {t(msg`Connected`)}
                      </Badge>
                    </Show>
                  </div>

                  <Show
                    when={isLinked(provider)}
                    fallback={
                      <a
                        href={linkUrl(provider)}
                        class={buttonVariants({ variant: "outline", size: "sm" })}
                        data-testid={`link-${provider}`}
                      >
                        {t(msg`Connect`)}
                      </a>
                    }
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy() || isLastMethod(provider)}
                      title={
                        isLastMethod(provider)
                          ? t(msg`Set a password before unlinking your only sign-in method.`)
                          : undefined
                      }
                      onClick={() => setConfirmUnlink(provider)}
                      class="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      {t(msg`Disconnect`)}
                    </Button>
                  </Show>
                </div>
              )}
            </For>

            <Show when={!hasPassword() && totalMethods() <= 1}>
              <p class="text-xs text-muted-foreground">
                {t(
                  msg`This is your only sign-in method. Set a password to be able to disconnect it.`,
                )}
              </p>
            </Show>
          </ServerDataGate>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmUnlink() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmUnlink(null);
          }
        }}
        title={t(msg`Disconnect account?`)}
        description={t(
          msg`You will no longer be able to sign in with this provider. You can reconnect it anytime.`,
        )}
        confirmLabel={t(msg`Disconnect`)}
        cancelLabel={t(msg`Cancel`)}
        onConfirm={() => {
          const provider = confirmUnlink();
          if (provider) {
            void handleUnlink(provider);
          }
        }}
      />
    </Show>
  );
}
